// frontend/src/pages/PerfilPublico.jsx
// ✅ Perfil público coeso com PerfilPessoal + aba Avaliações (sócio avalia, parceiro/público vê)
// ✅ + 1 avaliação por sócio (docId = avaliadorUid)
// ✅ + viewer completo (nome/foto) via /users/{uid}
// ✅ Usa o mesmo “Google-like” do PerfilPessoal (BrazaoGoogleRating) — shields

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  onSnapshot,
  setDoc,
  serverTimestamp,
  orderBy,
  limit,
} from 'firebase/firestore';

import { db } from '../services/firebase';
import { useAuth } from '../context/AuthContext';

import PropertyCard from '../components/PropertyCard';
import ListaIncorporadoras from '../components/ListaIncorporadoras';

// ✅ Mesmo brazão do PerfilPessoal (estilo Google, com Shields)
import BrazaoGoogleRating from '../components/avaliacao/BrazaoAvaliacoesButton';

import {
  Building2,
  MapPin,
  BadgePercent,
  Phone,
  ShieldCheck,
} from 'lucide-react';

/* =========================
   Helpers
========================= */
const formatarEspecialidade = (valor) => {
  switch (valor) {
    case 'residencial':
      return 'Imóveis residenciais';
    case 'comercial':
      return 'Imóveis comerciais';
    case 'alto_padrao':
      return 'Imóveis de alto padrão';
    case 'empreendimentos':
      return 'Empreendimentos';
    case 'rural':
      return 'Imóveis rurais';
    default:
      return valor;
  }
};

function toMillis(v) {
  if (!v) return 0;
  if (typeof v?.toMillis === 'function') return v.toMillis();
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const d = new Date(v);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  }
  if (typeof v?.toDate === 'function') {
    const d = v.toDate();
    return d instanceof Date ? d.getTime() : 0;
  }
  return 0;
}

function statusRank(p) {
  const s = String(p?.statusNegocio || 'ativo').toLowerCase();
  return s === 'ativo' ? 0 : 1;
}

const PRESETS_POSITIVO = [
  'Cumpriu tudo que foi combinado',
  'Comunicação clara e constante',
  'Agregou valor real à sociedade',
  'Trabalharia novamente sem ressalvas',
  'Postura ética e profissional',
];

const PRESETS_NEUTRO = [
  'Cumpriu parcialmente o combinado',
  'Comunicação poderia ser melhor',
];

const PRESETS_NEGATIVO = [
  'Demora a responder',
  'Pouco disponível',
  'Tivemos atritos',
];

function clamp01(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.min(10, Math.max(0, x));
}

function calcMedia(crit) {
  const vals = [
    clamp01(crit?.etica),
    clamp01(crit?.compromisso),
    clamp01(crit?.comunicacao),
    clamp01(crit?.postura),
    clamp01(crit?.tempoResposta),
  ];
  const sum = vals.reduce((a, b) => a + b, 0);
  return sum / vals.length;
}

/* =========================
   UI
========================= */
function NotaRow({ label, value, onChange, readOnly = false }) {
  const v = clamp01(value);
  const vv = Math.round(v); // ✅ evita value decimal no <select>

  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <div className="text-sm text-slate-700">{label}</div>

      {readOnly ? (
        <div className="text-sm font-semibold text-slate-900 w-12 text-right">
          {v.toFixed(1)}
        </div>
      ) : (
        <select
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
          value={String(vv)}
          onChange={(e) => onChange?.(Number(e.target.value))}
        >
          {Array.from({ length: 11 }).map((_, i) => (
            <option key={i} value={i}>
              {i}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

function Pill({ children, tone = 'neutral' }) {
  const tones = {
    neutral: 'bg-slate-100 text-slate-700 border-slate-200',
    good: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    warn: 'bg-amber-50 text-amber-700 border-amber-200',
    bad: 'bg-rose-50 text-rose-700 border-rose-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
  };

  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border ${
        tones[tone] || tones.neutral
      }`}
    >
      {children}
    </span>
  );
}

/* =========================
   Avaliações Panel (com create se podeAvaliar)
========================= */
function AvaliacoesPanel({
  perfilId,
  podeAvaliar,
  viewer,
  nomePerfil,
  onVoltar,
}) {
  const [loading, setLoading] = useState(true);
  const [itens, setItens] = useState([]);

  // Form
  const [openForm, setOpenForm] = useState(false);
  const [crit, setCrit] = useState({
    etica: 0,
    compromisso: 0,
    comunicacao: 0,
    postura: 0,
    tempoResposta: 0,
  });
  const [presetSelecionado, setPresetSelecionado] = useState('');
  const [comentario, setComentario] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!perfilId) return;

    const ref = collection(db, 'avaliacoes', perfilId, 'itens');
    const q = query(ref, orderBy('criadoEm', 'desc'), limit(50));

    const unsub = onSnapshot(
      q,
      (snap) => {
        setItens(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Erro ao ler avaliações:', err);
        setItens([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [perfilId]);

  const stats = useMemo(() => {
    if (!itens?.length) {
      return {
        count: 0,
        media: 0,
        breakdown: {
          etica: 0,
          compromisso: 0,
          comunicacao: 0,
          postura: 0,
          tempoResposta: 0,
        },
      };
    }

    const sum = {
      etica: 0,
      compromisso: 0,
      comunicacao: 0,
      postura: 0,
      tempoResposta: 0,
      media: 0,
    };

    itens.forEach((r) => {
      const c = r?.criterios || {};
      sum.etica += clamp01(c.etica);
      sum.compromisso += clamp01(c.compromisso);
      sum.comunicacao += clamp01(c.comunicacao);
      sum.postura += clamp01(c.postura);
      sum.tempoResposta += clamp01(c.tempoResposta);
      sum.media += Number(r?.media ?? calcMedia(c));
    });

    const count = itens.length;
    return {
      count,
      media: sum.media / count,
      breakdown: {
        etica: sum.etica / count,
        compromisso: sum.compromisso / count,
        comunicacao: sum.comunicacao / count,
        postura: sum.postura / count,
        tempoResposta: sum.tempoResposta / count,
      },
    };
  }, [itens]);

  const isReferencia = useMemo(
    () => stats.count >= 5 && stats.media >= 9.0,
    [stats]
  );

  // 1 avaliação por sócio: docId = viewer.uid
  const jaAvaliou = useMemo(() => {
    if (!viewer?.uid) return false;
    return itens.some(
      (r) => r?.avaliadorUid === viewer.uid || r?.id === viewer.uid
    );
  }, [itens, viewer?.uid]);

  async function salvarAvaliacao() {
    if (!podeAvaliar || !viewer?.uid || !perfilId) return;
    if (viewer.uid === perfilId) return;
    if (jaAvaliou) {
      alert('Você já avaliou este sócio.');
      return;
    }

    try {
      setSaving(true);

      const criterios = {
        etica: clamp01(crit.etica),
        compromisso: clamp01(crit.compromisso),
        comunicacao: clamp01(crit.comunicacao),
        postura: clamp01(crit.postura),
        tempoResposta: clamp01(crit.tempoResposta),
      };

      const media = calcMedia(criterios);

      const refDoc = doc(db, 'avaliacoes', perfilId, 'itens', viewer.uid);

      await setDoc(
        refDoc,
        {
          perfilId,
          avaliadorUid: viewer.uid,
          avaliadorNome: viewer?.nome || viewer?.displayName || 'Sócio',
          avaliadorFoto:
            viewer?.foto ||
            viewer?.fotoPerfil?.url ||
            viewer?.fotoPerfil ||
            null,
          vinculo: 'socio',
          criterios,
          media,
          preset: presetSelecionado || null,
          comentario: comentario?.trim() || null,
          criadoEm: serverTimestamp(),
        },
        { merge: false }
      );

      // reset
      setCrit({
        etica: 0,
        compromisso: 0,
        comunicacao: 0,
        postura: 0,
        tempoResposta: 0,
      });
      setPresetSelecionado('');
      setComentario('');
      setOpenForm(false);
    } catch (e) {
      console.error('Erro ao salvar avaliação:', e);
      alert('Não foi possível salvar sua avaliação.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            onClick={onVoltar}
            className="text-sm font-medium text-slate-700 hover:text-black"
            type="button"
          >
            ← Voltar
          </button>

          <h3 className="text-base font-semibold text-slate-900">Avaliações</h3>

          {isReferencia && (
            <Pill tone="blue">
              <ShieldCheck size={14} />
              Referência
            </Pill>
          )}
        </div>

        {podeAvaliar && !jaAvaliou && (
          <button
            onClick={() => setOpenForm((v) => !v)}
            className="text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50"
            type="button"
          >
            {openForm ? 'Fechar' : 'Avaliar sócio'}
          </button>
        )}

        {podeAvaliar && jaAvaliou && (
          <Pill tone="neutral">
            <ShieldCheck size={14} /> Você já avaliou
          </Pill>
        )}
      </div>

      {/* Summary */}
      <div className="mt-4 grid md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="text-xs text-slate-500">Média geral</div>
          <div className="mt-1 flex items-end gap-2">
            <div className="text-3xl font-semibold text-slate-900">
              {stats.media ? stats.media.toFixed(1) : '—'}
            </div>
            <div className="text-sm text-slate-500 pb-1">/ 10</div>
          </div>
          <div className="mt-2 text-xs text-slate-500">
            {stats.count} validaç{stats.count === 1 ? 'ão' : 'ões'}
          </div>
        </div>

        <div className="md:col-span-2 bg-white border border-slate-200 rounded-2xl p-4">
          <div className="text-xs text-slate-500">Critérios</div>
          <div className="mt-2 divide-y divide-slate-100">
            <NotaRow label="Ética" value={stats.breakdown.etica} readOnly />
            <NotaRow
              label="Compromisso"
              value={stats.breakdown.compromisso}
              readOnly
            />
            <NotaRow
              label="Comunicação"
              value={stats.breakdown.comunicacao}
              readOnly
            />
            <NotaRow label="Postura" value={stats.breakdown.postura} readOnly />
            <NotaRow
              label="Tempo de resposta"
              value={stats.breakdown.tempoResposta}
              readOnly
            />
          </div>
        </div>
      </div>

      {/* Form */}
      {openForm && podeAvaliar && !jaAvaliou && (
        <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-4">
          <div className="text-sm font-semibold text-slate-900">
            Avaliar {nomePerfil || 'corretor'}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Notas de 0 a 10. Seja justo: isso aumenta a segurança das
            sociedades.
          </p>

          <div className="mt-3 divide-y divide-slate-100">
            <NotaRow
              label="Ética"
              value={crit.etica}
              onChange={(v) => setCrit((p) => ({ ...p, etica: v }))}
            />
            <NotaRow
              label="Compromisso"
              value={crit.compromisso}
              onChange={(v) => setCrit((p) => ({ ...p, compromisso: v }))}
            />
            <NotaRow
              label="Comunicação"
              value={crit.comunicacao}
              onChange={(v) => setCrit((p) => ({ ...p, comunicacao: v }))}
            />
            <NotaRow
              label="Postura"
              value={crit.postura}
              onChange={(v) => setCrit((p) => ({ ...p, postura: v }))}
            />
            <NotaRow
              label="Tempo de resposta"
              value={crit.tempoResposta}
              onChange={(v) => setCrit((p) => ({ ...p, tempoResposta: v }))}
            />
          </div>

          {/* Presets */}
          <div className="mt-4">
            <div className="text-xs text-slate-500 mb-2">
              Comentários rápidos (opcional)
            </div>

            <div className="flex flex-wrap gap-2">
              {PRESETS_POSITIVO.map((t) => (
                <button
                  key={t}
                  onClick={() => setPresetSelecionado(t)}
                  className={`text-xs px-3 py-1.5 rounded-full border ${
                    presetSelecionado === t
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                  }`}
                  type="button"
                >
                  {t}
                </button>
              ))}

              {PRESETS_NEUTRO.map((t) => (
                <button
                  key={t}
                  onClick={() => setPresetSelecionado(t)}
                  className={`text-xs px-3 py-1.5 rounded-full border ${
                    presetSelecionado === t
                      ? 'bg-amber-50 border-amber-200 text-amber-800'
                      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                  }`}
                  type="button"
                >
                  {t}
                </button>
              ))}

              {PRESETS_NEGATIVO.map((t) => (
                <button
                  key={t}
                  onClick={() => setPresetSelecionado(t)}
                  className={`text-xs px-3 py-1.5 rounded-full border ${
                    presetSelecionado === t
                      ? 'bg-rose-50 border-rose-200 text-rose-800'
                      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                  }`}
                  type="button"
                >
                  {t}
                </button>
              ))}
            </div>

            <textarea
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              placeholder="Escreva um comentário (opcional)..."
              className="mt-3 w-full min-h-[96px] border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-200"
            />
          </div>

          <div className="mt-4 flex items-center justify-end gap-2">
            <button
              onClick={() => setOpenForm(false)}
              className="text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50"
              type="button"
            >
              Cancelar
            </button>
            <button
              onClick={salvarAvaliacao}
              disabled={saving}
              className="text-sm px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-black disabled:opacity-60"
              type="button"
            >
              {saving ? 'Salvando...' : 'Publicar avaliação'}
            </button>
          </div>
        </div>
      )}

      {/* Lista */}
      <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-4">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-900">
            Comentários
          </div>
          <Pill tone="neutral">
            <ShieldCheck size={14} /> {stats.count}
          </Pill>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500 mt-3">
            Carregando avaliações...
          </p>
        ) : itens.length === 0 ? (
          <p className="text-sm text-slate-500 mt-3">
            Ainda não há avaliações.
          </p>
        ) : (
          <div className="mt-3 space-y-3">
            {itens.map((r) => {
              const c = r?.criterios || {};
              const media = Number(r?.media ?? calcMedia(c));
              return (
                <div
                  key={r.id}
                  className="border border-slate-200 rounded-2xl p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <img
                        src={r?.avaliadorFoto || '/placeholder-user.jpg'}
                        alt={r?.avaliadorNome || 'Avaliador'}
                        className="w-9 h-9 rounded-full object-cover"
                      />
                      <div>
                        <div className="text-sm font-semibold text-slate-900">
                          {r?.avaliadorNome || 'Sócio'}
                        </div>
                        <div className="text-xs text-slate-500">
                          {r?.vinculo === 'socio' ? 'Sócio' : 'Corretor'}
                        </div>
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-sm font-semibold text-slate-900">
                        {media.toFixed(1)}
                        <span className="text-xs text-slate-500">/10</span>
                      </div>
                      <div className="text-xs text-slate-500">
                        {r?.criadoEm?.toDate
                          ? r.criadoEm.toDate().toLocaleDateString()
                          : ''}
                      </div>
                    </div>
                  </div>

                  {r?.preset && (
                    <div className="mt-2">
                      <Pill tone="neutral">{r.preset}</Pill>
                    </div>
                  )}

                  {r?.comentario && (
                    <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                      {r.comentario}
                    </p>
                  )}

                  <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                    <Pill tone="neutral">
                      Ética: {clamp01(c.etica).toFixed(0)}
                    </Pill>
                    <Pill tone="neutral">
                      Compromisso: {clamp01(c.compromisso).toFixed(0)}
                    </Pill>
                    <Pill tone="neutral">
                      Comunicação: {clamp01(c.comunicacao).toFixed(0)}
                    </Pill>
                    <Pill tone="neutral">
                      Postura: {clamp01(c.postura).toFixed(0)}
                    </Pill>
                    <Pill tone="neutral">
                      Resposta: {clamp01(c.tempoResposta).toFixed(0)}
                    </Pill>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* =========================
   Página: PerfilPublico
========================= */
export default function PerfilPublico(props) {
  const params = useParams();
  const id = props?.id || params?.id;

  const navigate = useNavigate();
  const { usuario } = useAuth();

  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState(null);
  const [imoveis, setImoveis] = useState([]);
  const [incorporadoras, setIncorporadoras] = useState([]);

  const [qtdSocios, setQtdSocios] = useState(0);
  const [qtdParcerias, setQtdParcerias] = useState(0);

  const [isSocio, setIsSocio] = useState(false);
  const [isParceiro, setIsParceiro] = useState(false);

  // Toggle: quando entra em Avaliações, some a lista de imóveis
  const [modo, setModo] = useState('imoveis'); // 'imoveis' | 'avaliacoes'

  // viewer completo (nome/foto) via users/{uid}
  const [viewerProfile, setViewerProfile] = useState(null);

  // stats do brazão (do perfil visitado)
  const [brazaoStats, setBrazaoStats] = useState({ count: 0, media: 0 });

  useEffect(() => {
    let alive = true;

    (async () => {
      const uid = usuario?.uid;
      if (!uid) {
        if (alive) setViewerProfile(null);
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'users', uid));
        if (!alive) return;
        setViewerProfile(snap.exists() ? snap.data() : null);
      } catch (e) {
        console.error('Erro ao carregar viewer profile:', e);
        if (alive) setViewerProfile(null);
      }
    })();

    return () => {
      alive = false;
    };
  }, [usuario?.uid]);

  useEffect(() => {
    let unsub = () => {};
    let ativo = true;

    (async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      setLoading(true);

      unsub = onSnapshot(
        doc(db, 'users', id),
        (snap) => {
          if (!ativo) return;
          setDados(snap.exists() ? snap.data() : null);
        },
        (err) => {
          console.error('Erro ao ler perfil:', err);
          setDados(null);
        }
      );

      setLoading(false);
    })();

    return () => {
      ativo = false;
      unsub();
    };
  }, [id]);

  // stats do brazão do perfil visitado
  useEffect(() => {
    if (!id) return;

    const ref = collection(db, 'avaliacoes', id, 'itens');
    const q = query(ref, orderBy('criadoEm', 'desc'), limit(50));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const itens = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (!itens.length) {
          setBrazaoStats({ count: 0, media: 0 });
          return;
        }
        let sum = 0;
        itens.forEach((r) => {
          const c = r?.criterios || {};
          sum += Number(r?.media ?? calcMedia(c));
        });
        setBrazaoStats({ count: itens.length, media: sum / itens.length });
      },
      (err) => {
        console.error('Erro ao ler stats do brazão (perfil visitado):', err);
        setBrazaoStats({ count: 0, media: 0 });
      }
    );

    return () => unsub();
  }, [id]);

  // Verifica vínculo (sócio/parceiro) e carrega contagens do perfil visitado
  useEffect(() => {
    let alive = true;

    async function run() {
      if (!id) return;

      try {
        // Contagem de sócios do perfil visitado (aceitos)
        const qS1 = query(
          collection(db, 'socios'),
          where('status', '==', 'accepted'),
          where('criador', '==', id)
        );
        const qS2 = query(
          collection(db, 'socios'),
          where('status', '==', 'accepted'),
          where('convidado', '==', id)
        );

        const [s1, s2] = await Promise.all([getDocs(qS1), getDocs(qS2)]);
        const sociosUids = Array.from(
          new Set(
            [...s1.docs, ...s2.docs].map((d) => {
              const data = d.data();
              return data.criador === id ? data.convidado : data.criador;
            })
          )
        );
        if (alive) setQtdSocios(sociosUids.length);

        // Contagem de parcerias do perfil visitado (modelo novo + legado)
        const qP1 = query(
          collection(db, 'parcerias'),
          where('status', '==', 'accepted'),
          where('criador', '==', id)
        );
        const qP2 = query(
          collection(db, 'parcerias'),
          where('status', '==', 'accepted'),
          where('convidado', '==', id)
        );
        const qP3 = query(
          collection(db, 'parcerias'),
          where('status', '==', 'accepted'),
          where('from', '==', id)
        );
        const qP4 = query(
          collection(db, 'parcerias'),
          where('status', '==', 'accepted'),
          where('to', '==', id)
        );

        const [p1, p2, p3, p4] = await Promise.all([
          getDocs(qP1),
          getDocs(qP2),
          getDocs(qP3),
          getDocs(qP4),
        ]);

        const parceiros = new Set();
        p1.docs.forEach((d) => parceiros.add(d.data()?.convidado));
        p2.docs.forEach((d) => parceiros.add(d.data()?.criador));
        p3.docs.forEach((d) => parceiros.add(d.data()?.to));
        p4.docs.forEach((d) => parceiros.add(d.data()?.from));

        if (alive) setQtdParcerias(parceiros.size);

        // Vínculo do viewer com o perfil visitado
        const viewerUid = usuario?.uid;
        if (!viewerUid) {
          if (alive) {
            setIsSocio(false);
            setIsParceiro(false);
          }
          return;
        }

        // é sócio?
        const qsA = query(
          collection(db, 'socios'),
          where('status', '==', 'accepted'),
          where('criador', '==', viewerUid),
          where('convidado', '==', id)
        );
        const qsB = query(
          collection(db, 'socios'),
          where('status', '==', 'accepted'),
          where('criador', '==', id),
          where('convidado', '==', viewerUid)
        );
        const [sa, sb] = await Promise.all([getDocs(qsA), getDocs(qsB)]);
        const socio = !sa.empty || !sb.empty;

        // é parceiro? (novo + legado)
        const qpA = query(
          collection(db, 'parcerias'),
          where('status', '==', 'accepted'),
          where('criador', '==', viewerUid),
          where('convidado', '==', id)
        );
        const qpB = query(
          collection(db, 'parcerias'),
          where('status', '==', 'accepted'),
          where('criador', '==', id),
          where('convidado', '==', viewerUid)
        );
        const qpC = query(
          collection(db, 'parcerias'),
          where('status', '==', 'accepted'),
          where('from', '==', viewerUid),
          where('to', '==', id)
        );
        const qpD = query(
          collection(db, 'parcerias'),
          where('status', '==', 'accepted'),
          where('from', '==', id),
          where('to', '==', viewerUid)
        );

        const [pa, pb, pc, pd] = await Promise.all([
          getDocs(qpA),
          getDocs(qpB),
          getDocs(qpC),
          getDocs(qpD),
        ]);
        const parceiro = !pa.empty || !pb.empty || !pc.empty || !pd.empty;

        if (alive) {
          setIsSocio(socio);
          setIsParceiro(parceiro);
        }
      } catch (e) {
        console.error('Erro ao verificar vínculo/contagens:', e);
      }
    }

    run();
    return () => {
      alive = false;
    };
  }, [id, usuario?.uid]);

  // Incorporadoras do perfil visitado
  useEffect(() => {
    let alive = true;

    async function fetchIncorporadoras() {
      if (!id) return;
      try {
        const q = query(
          collection(db, 'incorporadoras'),
          where('usuarioId', '==', id)
        );
        const snap = await getDocs(q);
        if (alive)
          setIncorporadoras(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error('Erro ao buscar incorporadoras:', e);
        if (alive) setIncorporadoras([]);
      }
    }

    fetchIncorporadoras();
    return () => {
      alive = false;
    };
  }, [id]);

  // Imóveis do perfil visitado:
  // - Sócio vê no perfil se compartilharComSocios=true
  // - Parceiro não vê no perfil
  useEffect(() => {
    let alive = true;

    async function fetchImoveisPublicos() {
      if (!id) return;

      try {
        if (!isSocio) {
          if (alive) setImoveis([]);
          return;
        }

        const imoveisRef = collection(db, 'imoveis');
        const snap = await getDocs(
          query(
            imoveisRef,
            where('criadoPor.uid', '==', id),
            where('compartilharComSocios', '==', true)
          )
        );

        const arr = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          origem: 'socio',
          ownerId: d.data()?.criadoPor?.uid,
        }));

        if (alive) setImoveis(arr);
      } catch (e) {
        console.error('Erro ao buscar imóveis do perfil visitado:', e);
        if (alive) setImoveis([]);
      }
    }

    fetchImoveisPublicos();
    return () => {
      alive = false;
    };
  }, [id, isSocio]);

  const imoveisOrdenados = useMemo(() => {
    return [...imoveis].sort((a, b) => {
      const ra = statusRank(a);
      const rb = statusRank(b);
      if (ra !== rb) return ra - rb;

      const ta = toMillis(
        a?.updatedAt ||
          a?.atualizadoEm ||
          a?.updatedEm ||
          a?.ultimaAtualizacao ||
          a?.createdAt ||
          a?.criadoEm ||
          a?.data
      );
      const tb = toMillis(
        b?.updatedAt ||
          b?.atualizadoEm ||
          b?.updatedEm ||
          b?.ultimaAtualizacao ||
          b?.createdAt ||
          b?.criadoEm ||
          b?.data
      );
      return tb - ta;
    });
  }, [imoveis]);

  // viewer (completo)
  const viewer = useMemo(() => {
    return {
      uid: usuario?.uid,
      displayName: usuario?.displayName,
      nome: viewerProfile?.nome,
      foto: viewerProfile?.fotoPerfil?.url || viewerProfile?.fotoPerfil || null,
      fotoPerfil: viewerProfile?.fotoPerfil,
    };
  }, [usuario, viewerProfile]);

  if (loading) return <p className="p-6">Carregando perfil...</p>;
  if (!dados) return <p className="p-6">Perfil não encontrado.</p>;

  const podeVerContato = isSocio || isParceiro;
  const podeAvaliar = isSocio;

  // ✅ normaliza para 0..5 (dados internos são 0..10)
  const mediaParaExibir = brazaoStats.media ? brazaoStats.media / 2 : 0;

  return (
    <div className="relative">
      <div className="max-w-7xl mx-auto flex p-0 m-0">
        {/* COLUNA CENTRAL */}
        <div className="flex-1 space-y-4 p-6 pt-[92px]">
          {/* Cabeçalho do perfil */}
          <div className="pb-6">
            <div className="flex items-start gap-6 flex-wrap">
              <img
                src={
                  dados.fotoPerfil?.url ||
                  dados.fotoPerfil ||
                  '/placeholder-user.jpg'
                }
                alt="Foto de perfil"
                className="w-24 h-24 rounded-full object-cover"
              />

              <div className="flex-1 min-w-[200px]">
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-900">
                      {dados.nome || 'Nome não informado'}
                    </h2>

                    {/* ✅ Brazão igual PerfilPessoal (Google-like) */}
                    <div
                      className="mt-1 cursor-pointer hover:opacity-80 transition-opacity inline-flex"
                      onClick={() =>
                        setModo((m) =>
                          m === 'avaliacoes' ? 'imoveis' : 'avaliacoes'
                        )
                      }
                      title="Clique para ver avaliações"
                    >
                      <BrazaoGoogleRating
                        rating={mediaParaExibir}
                        total={brazaoStats.count}
                        variant="gold"
                        size={16}
                      />
                    </div>
                  </div>
                </div>

                {/* Contadores / vínculo */}
                <div className="flex gap-6 text-sm text-gray-800 mt-3">
                  <span
                    className="cursor-pointer hover:text-blue-600"
                    title="Contagem do corretor"
                  >
                    <strong>{qtdSocios}</strong> Sócios
                  </span>

                  <span
                    className="cursor-pointer hover:text-blue-600"
                    title="Contagem do corretor"
                  >
                    <strong>{qtdParcerias}</strong> Parcerias
                  </span>

                  <span className="text-slate-600">
                    {isSocio ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-emerald-500" />
                        Sócio
                      </span>
                    ) : isParceiro ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-amber-500" />
                        Parceiro
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-slate-300" />
                        Sem conexão
                      </span>
                    )}
                  </span>
                </div>

                {/* Informações adicionais */}
                <div className="mt-3 text-sm text-gray-600 space-y-1">
                  {dados.especialidade && (
                    <div className="flex items-center gap-2">
                      <Building2 size={16} />
                      {formatarEspecialidade(dados.especialidade)}
                    </div>
                  )}

                  {dados.endereco?.cidade && (
                    <div className="flex items-center gap-2">
                      <MapPin size={16} /> {dados.endereco?.cidade}
                      {dados.endereco?.estado
                        ? `, ${dados.endereco?.estado}`
                        : ''}
                    </div>
                  )}

                  {dados.creci && (
                    <div className="flex items-center gap-2">
                      <BadgePercent size={16} /> CRECI: {dados.creci}
                    </div>
                  )}

                  {podeVerContato && dados.telefone && (
                    <a
                      href={`https://wa.me/55${String(dados.telefone).replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-[#1e293b] hover:text-black font-medium"
                    >
                      <Phone size={16} /> Conversar no WhatsApp
                    </a>
                  )}
                </div>

                {dados.descricao && (
                  <p className="pt-3 text-sm text-gray-700 leading-relaxed max-w-3xl">
                    {dados.descricao}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Incorporadoras */}
          {incorporadoras?.length > 0 && (
            <div className="mt-8">
              <div className="border-t border-gray-200"></div>
              <div className="pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 text-left">
                  Empresas parceiras
                </h3>

                <ListaIncorporadoras
                  items={incorporadoras}
                  onEdit={() => {}}
                  onDeleted={() => {}}
                  readOnly
                />
              </div>
              <div className="border-t border-gray-200 mt-4"></div>
            </div>
          )}

          {/* CONTEÚDO DINÂMICO */}
          {modo === 'avaliacoes' ? (
            <AvaliacoesPanel
              perfilId={id}
              podeAvaliar={podeAvaliar}
              viewer={viewer}
              nomePerfil={dados?.nome}
              onVoltar={() => setModo('imoveis')}
            />
          ) : (
            <>
              {!isSocio ? (
                <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-4">
                  <p className="text-sm text-slate-700">
                    {isParceiro
                      ? 'Imóveis de parceiros aparecem na busca e na Home, não no perfil.'
                      : 'Conecte-se como sócio para visualizar os imóveis deste corretor no perfil.'}
                  </p>

                  <button
                    onClick={() => setModo('avaliacoes')}
                    className="mt-3 inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50"
                    type="button"
                  >
                    <ShieldCheck size={16} /> Ver Avaliações (Brazão)
                  </button>
                </div>
              ) : imoveisOrdenados.length === 0 ? (
                <p className="text-gray-500 mt-4">
                  Nenhum imóvel compartilhado.
                </p>
              ) : (
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
                  {imoveisOrdenados.map((imovel) => (
                    <PropertyCard key={imovel.id} property={imovel} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* LATERAL */}
        <div className="w-64 mt-[92px] pr-6">
          <div className="bg-white border border-slate-200 p-4 rounded-2xl">
            <div className="text-sm font-semibold text-slate-900">Ações</div>

            <div className="mt-3 space-y-2">
              <button
                onClick={() => setModo('avaliacoes')}
                className="w-full text-left text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 flex items-center gap-2"
                type="button"
              >
                <ShieldCheck size={16} /> Brazão / Avaliações
              </button>

              <button
                onClick={() => navigate('/buscar-socios')}
                className="w-full text-left text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50"
                type="button"
              >
                Buscar corretores
              </button>

              <div className="pt-2 text-xs text-slate-500">
                {isSocio
                  ? 'Você é sócio: pode avaliar.'
                  : isParceiro
                    ? 'Você é parceiro: pode ver avaliações.'
                    : 'Sem conexão: avaliações são públicas, imóveis não.'}
              </div>
            </div>
          </div>
        </div>
        {/* FIM LATERAL */}
      </div>
    </div>
  );
}
